import { MobileAppSubscription, UserSubscription } from '~/types/user-info-types'
import { Coupon } from '~/subscription/types/subscription.types'

export enum FAMILY_PROFILE_COLORS {
  BLUE = 'blue',
  ORANGE = 'orange',
  PINK = 'pink',
  PURPLE = 'purple',
  YELLOW = 'yellow',
  VIOLET = 'violet',
  LIGHT_BLUE = 'light-blue',
  GREEN = 'green',
  DARK_PURPLE = 'dark-blue',
  GREY = 'grey',
  INDIGO = 'indigo',

  // === Purple Variations ===
  PURPLE_LIGHT = 'purple-light',
  PURPLE_MEDIUM = 'purple-medium',
  PURPLE_STANDARD = 'purple-standard',
  PURPLE_DEEP = 'purple-deep',
  PURPLE_DARK = 'purple-dark',

  // === Yellow Variations ===
  YELLOW_PASTEL = 'yellow-pastel',
  YELLOW_SOFT = 'yellow-soft',
  YELLOW_STANDARD = 'yellow-standard',
  YELLOW_WARM = 'yellow-warm',
  YELLOW_GOLD = 'yellow-gold',

  // === Blue Variations ===
  BLUE_LIGHT = 'blue-light',
  BLUE_SOFT = 'blue-soft',
  BLUE_SKY = 'blue-sky',
  BLUE_STANDARD = 'blue-standard',
  BLUE_DEEP = 'blue-deep',
  BLUE_NAVY = 'blue-navy',
  BLUE_MIDNIGHT = 'blue-midnight',

  // === Pink Variations ===
  PINK_LIGHT = 'pink-light',
  PINK_SOFT = 'pink-soft',
  PINK_STANDARD = 'pink-standard',
  PINK_BRIGHT = 'pink-bright',
  PINK_DEEP = 'pink-deep',

  // === Orange Variations ===
  ORANGE_LIGHT = 'orange-light',
  ORANGE_SOFT = 'orange-soft',
  ORANGE_STANDARD = 'orange-standard',
  ORANGE_DEEP = 'orange-deep',
  ORANGE_DARK = 'orange-dark',

  // === Green Variations ===
  GREEN_LIGHT = 'green-light',
  GREEN_SOFT = 'green-soft',
  GREEN_STANDARD = 'green-standard',
  GREEN_MEDIUM = 'green-medium',
  GREEN_DEEP = 'green-deep',
  GREEN_DARK = 'green-dark',
}

export enum FAMILY_ROLE {
  LEARNER = 'learner',
  ADMIN = 'admin',
}

export enum FAMILY_LICENSE_STATUS {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_REMOVAL = 'pending_removal',
  REMOVED = 'removed',
}

export enum FAMILY_CREATE_PROFILE_STEPS {
  SEND_INVITE = 'send-invite',
  CREATE_PROFILE = 'create-profile',
}

export interface AddFamilyMemberPayload {
  email?: string
  nickname: string
  password?: string
  country?: string
  birthYear?: string
  pin?: string
}

export interface SwitchProfilePayload {
  userId: number
  pin?: string
}

export interface AddFamilyMemberResponse {
  nickname: string
  addedBy: number
  userId: number
}

export interface FamilyData {
  avatarPngUrl?: string
  avatarUrl?: string
  hasLogIn: boolean
  hasPin: boolean
  nickname: string
  payingUserId: number
  profileColor: FAMILY_PROFILE_COLORS
  role: FAMILY_ROLE
  licenseStatus: FAMILY_LICENSE_STATUS
  userId: number
  stripeSubscription?: UserSubscription[]
  validMobileAppSubscription?: MobileAppSubscription
}

export interface FamilyInfo {
  userCount: number
  activeMemberCount: number
  familyId: number
  licenseCount: number
  payingUserId: number
  subscriptionId: string
  removalInfo: {
    withUserPendingRemoval: number
    withoutUserPendingRemoval: number
  }
}

export interface InviteCreateResponse {
  expiresAt: Date
  inviteCode: string
}
export interface FamilyInvite {
  adminId: number
  familyId: number
  inviteCode: string
  email?: string
  userId?: number
  expiresAt: Date
  canceledAt?: Date
  acceptedAt?: Date
}

export interface FamilyManageLicenseData {
  isTrialing: boolean
  /** Major units — this endpoint divides on the API side, unlike the Stripe amounts elsewhere. */
  proratedCost: number
  /** Major units. What Stripe will actually take, after any account credit; `proratedCost` ignores it. */
  amountDueToday?: number
  prorationDate: number
  periodEnd: number
  /** Whether the requested discount actually covers the target price. */
  couponApplied?: boolean
}

export interface ManageLicenseData {
  nrOfNewLicenses: number
  addingAdminLicense?: boolean
  userId?: number // userId of the license being reactivated
}

export interface UseSwitchProfile {
  switchProfile: ({
    userId,
    pin,
  }: {
    userId: number
    pin?: string
  }) => Promise<{ errorCode: number; message: string; type: 'error' } | { success: boolean }>
  // True only after tokens, user, and grade refetch have finished
  isSwitchSessionReady: boolean
  isSwitchingProfile: boolean
}

export type ReactivationPartner = 'dnb' | 'knowit' | null

export interface PartnerPriceTier {
  priceId: string
  basePrice: number
  licensePrice: number
  currency: string
}

export interface PreviousCoupon {
  code: string
  valid: boolean
}

export type CouponDetails = Coupon & { promotionId?: string; code?: string }

export interface RepurchasePreviewData {
  basePrice: number
  licensePrice: number
  currency: string
  interval: string
  priceId: string
  paymentGateway: string
  partner: ReactivationPartner
  previousCoupon: PreviousCoupon | null
  partnerPrices?: {
    monthly?: PartnerPriceTier
    yearly?: PartnerPriceTier
  }
  // Populated only when the supplied coupon is applicable to the CANCELLED sub's price — not to the
  // price the user picks in the reactivation picker. It is therefore not an applicability signal for
  // that picked price; `getStripePrice(priceId, currency)` is (see selectPriceValidatedCoupon).
  couponDetails?: CouponDetails
}
